import { config } from "../../shared/config/environment";
import { fetchWithRefresh, type RefreshTokenFn } from "../../shared/api/fetchWithRefresh";
import type { Finding, FindingStatus } from "../types";

/** One finding the frontend has already evaluated deterministically
 *  (AIVA-7506's frontend rework) — shaped for the bulk-create route.
 *  Mirrors the backend `FindingCreateIn` (API/models/assisted_automation.py). */
export interface FindingCreate {
  rule_id: string;
  host?: string;
  problem?: string;
  comment?: string;
  fix_handler?: string;
  fix_params?: Record<string, unknown>;
  confidence?: number | null;
}

// The document-review findings endpoints live on the Python FastAPI service
// (apps/backend/ai/API/routes/v2/assisted_automation.py — host-agnostic by
// design, currently populated only by Word's cross-reference/defined-terms
// review), mirroring src/approvals/services/api.ts's routing.

/** Mirrors `buildHeaders` in src/approvals/services/api.ts. */
function buildHeaders(apiToken: string | null, contentType?: string): Record<string, string> {
  const headers: Record<string, string> = {};
  if (contentType) {
    headers["Content-Type"] = contentType;
  }
  if (apiToken) {
    headers["Authorization"] = `Bearer ${apiToken}`;
  }
  return headers;
}

/** Fetch every finding for one document-review thread. */
export async function listFindings(
  threadId: string,
  apiToken: string | null,
  refresh?: RefreshTokenFn,
  signal?: AbortSignal,
): Promise<Finding[]> {
  const response = await fetchWithRefresh(
    `${config.aiApiBaseUrl}/api/v2/assisted-automation/threads/${encodeURIComponent(threadId)}/findings`,
    {
      headers: buildHeaders(apiToken),
      // Mirrors listActivities: a stale cached GET must never hide a just-applied
      // finding's fresh status from a subsequent refresh.
      cache: "no-store",
      signal,
    },
    refresh,
  );
  if (!response.ok) {
    throw new Error(`Failed to load findings: ${response.status}`);
  }
  return response.json() as Promise<Finding[]>;
}

/** The two PATCH calls below differ only in their body and in what they call
 *  a failure, so the request itself lives here — one place that knows this
 *  endpoint's shape, rather than two that have to be kept in step. */
async function patchFinding(
  threadId: string,
  findingId: string,
  body: Record<string, unknown>,
  failureLabel: string,
  apiToken: string | null,
  refresh?: RefreshTokenFn,
): Promise<Finding> {
  const response = await fetchWithRefresh(
    `${config.aiApiBaseUrl}/api/v2/assisted-automation/threads/${encodeURIComponent(threadId)}/findings/${encodeURIComponent(findingId)}`,
    {
      method: "PATCH",
      headers: buildHeaders(apiToken, "application/json"),
      body: JSON.stringify(body),
    },
    refresh,
  );
  if (!response.ok) {
    throw new Error(`${failureLabel}: ${response.status}`);
  }
  return response.json() as Promise<Finding>;
}

/**
 * Record an anchor the client just re-established for a finding the anchor
 * step originally missed (AIVA-8209), moving it `pending` -> `anchored`.
 *
 * This has to reach the server BEFORE the fix's own outcome does. The server
 * refuses to mark a `pending` finding applied/verified — rightly, since that
 * records an outcome for something never located — so without this call a
 * repaired fix writes to the document and then fails to persist with a 409,
 * leaving a real edit with no record of it and an Apply button still live to
 * make that same edit a second time.
 *
 * `expected` is passed through exactly as the caller determined it, which for
 * a finding that already had a fingerprint means its ORIGINAL one, never the
 * repair's fresh reading — see `applyWithAnchorRepair`.
 */
export async function patchFindingAnchor(
  threadId: string,
  findingId: string,
  anchorTag: string,
  expected: string | null,
  apiToken: string | null,
  refresh?: RefreshTokenFn,
): Promise<Finding> {
  const body = { status: "anchored", anchor_tag: anchorTag, expected };
  return patchFinding(threadId, findingId, body, "Failed to record the repaired anchor", apiToken, refresh);
}

/** Update a finding's status once the operator has applied or skipped its
 *  fix client-side. Etag-guarded server-side — a 409 (surfaced as a thrown
 *  error here) means another writer updated it first. */
export async function patchFindingStatus(
  threadId: string,
  findingId: string,
  status: FindingStatus,
  apiToken: string | null,
  refresh?: RefreshTokenFn,
  /** AIVA-8280: the id Word gave the comment this fix inserted, when it
   *  resolved by commenting. Lets a later scan ask whether that exact
   *  comment is still in the document, instead of comparing its text —
   *  which several findings of one rule routinely share. */
  commentId?: string,
): Promise<Finding> {
  const body = commentId ? { status, comment_id: commentId } : { status };
  return patchFinding(threadId, findingId, body, "Failed to update finding", apiToken, refresh);
}

/**
 * Persist findings the frontend already evaluated deterministically
 * (AIVA-7506's frontend rework — missing_hyperlink, broken_reference,
 * bracket_heading_mismatch, inconsistent_formatting, exhibit_schedule_mismatch's
 * unambiguous case, defined_term_section_mismatch, shortened_hyperlink_text,
 * defined_not_used, alphabetical_order, numbering_order, missing_defined_term,
 * ambiguous_reference) — never through the LLM. Called
 * from `word.docreview.scan`'s handler right after evaluation, before the
 * interrupt returns; the LLM only ever sees the 4 rules that still need a
 * judgment call. Best-effort: returns whichever findings actually
 * persisted, never throws for an individual lost etag race — a network
 * failure for the WHOLE request still throws, same as `listFindings`.
 *
 * ALWAYS posts, including with an empty `findings` array (AIVA-8280). This
 * call is not just a write — it is the boundary of a review cycle, and the
 * route it hits clears the PREVIOUS cycle's undecided findings before
 * writing this one's. Returning early on an empty array therefore skipped
 * the reset entirely whenever a check came back clean, leaving the previous
 * check's findings in the operator's pane beside this check's "no problems
 * found" answer. An empty POST is a real statement — "this check ran and
 * found nothing" — and is categorically different from never calling at
 * all, the same distinction the /review skill's `{"review": []}` payload
 * draws for the same reason. The one case that must still NOT post is a
 * scan superseded by a newer one, which its caller already guards
 * (handlers.ts's `isStillCurrent`) — an abandoned scan clearing a live
 * scan's findings is exactly what that guard exists to prevent.
 */
export async function bulkCreateFindings(
  threadId: string,
  findings: FindingCreate[],
  apiToken: string | null,
  refresh?: RefreshTokenFn,
): Promise<Finding[]> {
  const response = await fetchWithRefresh(
    `${config.aiApiBaseUrl}/api/v2/assisted-automation/threads/${encodeURIComponent(threadId)}/findings/bulk`,
    {
      method: "POST",
      headers: buildHeaders(apiToken, "application/json"),
      body: JSON.stringify(findings),
    },
    refresh,
  );
  if (!response.ok) {
    throw new Error(`Failed to persist findings: ${response.status}`);
  }
  return response.json() as Promise<Finding[]>;
}

/**
 * Tell the server which comments are currently in the document, so it can
 * reopen comment fixes the operator has since undone (AIVA-8280).
 *
 * Must run BEFORE `bulkCreateFindings`, which is where the server reads
 * finding statuses to decide what not to re-flag.
 *
 * Only comment fixes need this. A fix that rewrote the document is settled by
 * the scan itself — if the sweep re-found the location, the rewrite is gone.
 * A comment leaves the flagged text untouched, so the rule stays true forever
 * and the only way to tell "resolved" from "resolved then Ctrl+Z'd" is to look
 * at the document, which only this side can do.
 *
 * Best-effort by design: a failure here leaves every finding exactly as it is
 * (suppressed, i.e. today's behaviour) and must never take the scan down with
 * it — the review is still worth running without it.
 */
export async function reconcileFindings(
  threadId: string,
  commentTexts: string[],
  commentIds: string[],
  apiToken: string | null,
  refresh?: RefreshTokenFn,
): Promise<number> {
  const response = await fetchWithRefresh(
    `${config.aiApiBaseUrl}/api/v2/assisted-automation/threads/${encodeURIComponent(threadId)}/findings/reconcile`,
    {
      method: "POST",
      headers: buildHeaders(apiToken, "application/json"),
      body: JSON.stringify({ comment_texts: commentTexts, comment_ids: commentIds }),
    },
    refresh,
  );
  if (!response.ok) {
    throw new Error(`Failed to reconcile findings: ${response.status}`);
  }
  const body = (await response.json()) as { reopened?: number };
  return body.reopened ?? 0;
}
